import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useReducer } from 'react';

const UPDATE_ERROR = 'UPDATE_ERROR';
const RESET_ERROR = 'RESET_ERROR';
const UPDATE_FILE = 'UPDATE_FILE';
const RESET_FILE = 'RESET_FILE';

type InitialFileStateType = { preview: string; raw: File; name: string };
const initialFileState: InitialFileStateType = {
    preview: '',
    raw: null,
    name: ''
};
type InitialErrorStateType = { type: string; current: string | number; required: string | number };
const initialErrorState: InitialErrorStateType = { type: null, current: null, required: null };

type InitialConstraintsStateType = {
    minWidth: number;
    minHeight: number;
    maxSize: number;
    allowedExtensions: string[];
};
const initialConstraintsState: InitialConstraintsStateType = {
    minWidth: null,
    minHeight: null,
    maxSize: null,
    allowedExtensions: []
};

type InitialStateType = {
    inputId: string;
    file: InitialFileStateType;
    error: InitialErrorStateType;
    constraints: InitialConstraintsStateType;
};
const initialState: InitialStateType = {
    inputId: '',
    file: initialFileState,
    error: initialErrorState,
    constraints: initialConstraintsState
};

const FileUploaderContext = createContext<
    { fileUploaderData: InitialStateType; dispatch: Dispatch } | undefined
>(undefined);

type FileUploaderAction =
    | {
          type: typeof UPDATE_FILE;
          payload: Partial<InitialFileStateType>;
      }
    | {
          type: typeof RESET_FILE;
      }
    | {
          type: typeof UPDATE_ERROR;
          payload: InitialErrorStateType;
      }
    | {
          type: typeof RESET_ERROR;
      };

type Dispatch = (action: FileUploaderAction) => void;

const FileUploaderReducer = (state: InitialStateType, action: FileUploaderAction) => {
    switch (action.type) {
        case UPDATE_FILE: {
            return {
                ...state,
                file: { ...state.file, ...action.payload }
            };
        }
        case RESET_FILE: {
            return {
                ...state,
                file: initialFileState
            };
        }
        case UPDATE_ERROR: {
            return {
                ...state,
                error: action.payload
            };
        }
        case RESET_ERROR: {
            return {
                ...state,
                error: initialErrorState
            };
        }
    }
};

type FileUploaderProviderProps = {
    value: Pick<InitialStateType, 'inputId' | 'constraints'>;
    children: ReactNode;
};

const FileUploaderProvider = ({ value, children }: FileUploaderProviderProps) => {
    const [state, dispatch] = useReducer(FileUploaderReducer, { ...initialState, ...value });
    const dispatchFileUploader = useCallback(
        (args: FileUploaderAction) => dispatch(args),
        [dispatch]
    );
    const fileUploaderData = useMemo(() => state, [state]);

    return (
        <FileUploaderContext.Provider value={{ fileUploaderData, dispatch: dispatchFileUploader }}>
            {children}
        </FileUploaderContext.Provider>
    );
};

const useFileUploaderContext = () => {
    const context = useContext(FileUploaderContext);
    if (!context) {
        throw new Error(
            `FileUploader context cannot be rendered outside the FileUploader component`
        );
    }
    return context;
};

export { FileUploaderProvider, useFileUploaderContext };
